import type { ReactElement } from 'react';
import { CHANNEL_TYPES, ChannelType } from '@gr/core/distribution/channels/channel-types';
import { ErrorCode } from '@gr/core/errors';
import { useT } from '../../../../app/di';
import { Button } from '../../../../ui-kit/Button/Button';
import { FieldRow, FieldRowActions, FieldRowGrow } from '../../../../ui-kit/FieldRow/FieldRow';
import { FormAlert, FormSelectField, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';

export interface ChannelValues {
  type: ChannelType;
  name: string;
  url: string;
}

export interface ChannelFormProps {
  initial: ChannelValues;
  /** Тип выбирается только при создании (CHN-UC-1); у сохранённого канала он не меняется. */
  withType: boolean;
  submitLabel: string;
  /** Очистить название и ссылку после успеха (форма добавления). */
  clearOnSuccess?: boolean;
  onSubmit(values: ChannelValues): Promise<ServerOutcome>;
}

/** CHN-UC-1, CHN-UC-2: тип, название и ссылка канала. */
export function ChannelForm({ initial, withType, submitLabel, clearOnSuccess = false, onSubmit }: ChannelFormProps): ReactElement {
  const { t } = useT();
  const { form, submit } = useServerForm<ChannelValues>({
    defaultValues: initial,
    fields: ['type', 'name', 'url'],
    onSubmit,
    ...(clearOnSuccess ? { afterSuccess: (values: ChannelValues) => ({ ...values, name: '', url: '' }) } : {}),
  });
  const { control, formState } = form;
  const disabled = formState.isSubmitting;
  return (
    <form className="u-stack" onSubmit={submit} noValidate>
      <FormAlert form={form} />
      <FieldRow>
        {withType ? (
          <FormSelectField
            control={control}
            name="type"
            label={t('channels.type')}
            options={CHANNEL_TYPES.map((type) => ({ value: type, label: t(`channels.types.${type}`) }))}
            disabled={disabled}
          />
        ) : null}
        <FieldRowGrow>
          <FormTextField control={control} name="name" label={t('channels.name')} rules={{ required: ErrorCode.FieldRequired }} disabled={disabled} />
        </FieldRowGrow>
        <FieldRowGrow>
          <FormTextField control={control} name="url" label={t('channels.url')} type="url" placeholder="https://" disabled={disabled} />
        </FieldRowGrow>
        <FieldRowActions>
          <Button type="submit" variant={withType ? 'primary' : 'secondary'} icon={withType ? 'plus' : undefined} loading={disabled} disabled={!withType && !formState.isDirty}>
            {submitLabel}
          </Button>
        </FieldRowActions>
      </FieldRow>
    </form>
  );
}
